import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { BackBar } from '@/components/BackBar';
import { MinusIcon, PlusIcon } from '@/components/Icons';
import { Mark } from '@/components/Mark';
import { Screen } from '@/components/Screen';
import { useAppState } from '@/state/useAppState';
import { useT } from '@/state/useT';
import {
  KIDS_MAX_AGE,
  MAX_AGE,
  MIN_AGE,
  clampAge,
  isOpenEndedAge,
  skinForAge,
} from '@/theme/types';

const ALL_FOUR = { prayed: true, onTime: true, jamaah: true, takbirah: true };

export function Onboarding() {
  const { profile, previewSkin, saveProfile, updatePreferences } = useAppState();
  const navigate = useNavigate();
  const { t, number } = useT();
  // Reached from Settings as well as from first launch, so it edits rather
  // than always starting blank.
  const [name, setName] = useState(profile?.name ?? '');
  // A profile saved before the ceiling moved can hold an age above it, and
  // the stepper has no step for one: clamp on the way in so Younger moves the
  // number on the first tap rather than the third.
  const [age, setAge] = useState(() => clampAge(profile?.age ?? 8));

  const trimmed = name.trim();
  const skin = skinForAge(age);
  const openEnded = isOpenEndedAge(age);

  // Re-skin the whole screen as the age changes, and hand the skin back when
  // leaving so a visit from Settings does not strand a preview.
  useEffect(() => {
    previewSkin(skin);
    return () => previewSkin(null);
  }, [skin, previewSkin]);

  const submit = async () => {
    await saveProfile({ name: trimmed, age });
    // The age chooses the skin, so any earlier manual override is stale.
    await updatePreferences({ skinOverride: null });
    void navigate('/', { replace: true });
  };

  return (
    <Screen
      quietSky
      header={<BackBar title={t('onboarding.title')} />}
      foot={
        <>
          {/* A profile with no name is not recoverable: the welcome screen is
              only reachable while there is no profile at all. */}
          <button
            type="button"
            className="btn-p"
            aria-disabled={trimmed === ''}
            onClick={() => trimmed !== '' && void submit()}
          >
            {t('onboarding.continue')}
          </button>
          <div className="muted-note" style={{ textAlign: 'center' }}>
            {t('onboarding.privacy')}
          </div>
        </>
      }
    >
      <div className="sect" style={{ marginTop: 22 }}>
        {t('onboarding.nameLabel')}
      </div>
      <input
        className="field"
        style={{ marginTop: 8 }}
        placeholder={t('onboarding.namePlaceholder')}
        aria-label={t('onboarding.nameLabel')}
        value={name}
        autoComplete="off"
        onChange={(e) => setName(e.target.value)}
      />

      <div className="sect" style={{ marginTop: 22 }}>
        {t('onboarding.ageLabel')}
      </div>
      <div className="stepper" style={{ marginTop: 8 }}>
        <button
          type="button"
          className="stepb"
          aria-label={t('onboarding.younger')}
          onClick={() => setAge((a) => Math.max(MIN_AGE, a - 1))}
        >
          <MinusIcon />
        </button>
        {/* The top step is open-ended — "14+" — because every age above the
            threshold picks the same version, and the old exact ceiling of 17
            left anyone older with no honest answer. */}
        <div className="agebox">
          <span className="agenum">
            {openEnded ? t('onboarding.agePlus', { age: number(KIDS_MAX_AGE) }) : number(age)}
          </span>
          <span style={{ fontFamily: 'var(--fd)', fontSize: 15, fontWeight: 700 }}>
            {t('onboarding.yearsUnit', { count: openEnded ? MAX_AGE : age })}
          </span>
        </div>
        <button
          type="button"
          className="stepb"
          aria-label={t('onboarding.older')}
          onClick={() => setAge((a) => Math.min(MAX_AGE, a + 1))}
        >
          <PlusIcon />
        </button>
      </div>

      <div className="verdict" style={{ marginTop: 16 }} data-testid="verdict">
        <Mark mark={ALL_FOUR} size={34} />
        <span style={{ flexGrow: 1 }}>
          <span
            style={{
              display: 'block',
              fontFamily: 'var(--fd)',
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {t(skin === 'kids' ? 'onboarding.verdict.kids' : 'onboarding.verdict.adult', {
              who: trimmed === '' ? t('onboarding.namePlaceholder') : trimmed,
            })}
          </span>
          <span
            style={{
              display: 'block',
              fontSize: 12,
              lineHeight: 1.3,
              color: 'var(--panel-dim)',
              fontWeight: 600,
              marginTop: 2,
            }}
          >
            {t(skin === 'kids' ? 'onboarding.verdictNote.kids' : 'onboarding.verdictNote.adult', {
              age: number(skin === 'kids' ? KIDS_MAX_AGE : KIDS_MAX_AGE + 1),
            })}
          </span>
        </span>
      </div>
    </Screen>
  );
}
